/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	CircularProgress,
	TextField,
	FormHelperText,
	FormLabel,
	Box,
	Grid,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { getUnixTime } from 'date-fns';
import { taskTypes, getUrlFromPipeline } from '@/utils/constants';
import {
	useCreateTaskMutation,
	useUpdateTaskMutation,
} from '@/features/engagements/engagementsApi';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { AssociatedClientDealSelect } from '@/components/selects/AssociatedClientDealSelect';
import { PrioritySelect } from '@/components/selects/PrioritySelect';
import { CategorySelect } from '@/components/selects/CategorySelect';
import { OwnerSelect } from '@/components/selects/OwnerSelect';
import { showToast } from '@/features/toast/toastSlice';
import { useObjectContext } from '@/hooks/useObjectContext';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { triggerRefresh } from '@/features/table/tasksTableSlice';
import { validationSchema } from './validationSchema';
import { FieldValue } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import {
	useCreateEventMutation,
	useGetEventDataQuery,
	useGetUserByEmailQuery,
	useUpdateEventMutation,
} from '@/features/msGraph/msGraphApi';
import FolioCheckbox from '@/components/FolioCheckbox';
import outlookLogo from '@/assets/images/outlookLogo.png';
import PerformantTextField from '@/components/fields/PerformantTextField';
import TaskTypeSelect from './Fields/TaskTypeSelect';
import DateFields from './Fields/DateFields';
import MeetingFields from './Fields/MeetingFields';
import { useGetDealRowQuery } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { useGetClientPropertiesQuery } from '@/features/clients/clientsApi';
import FormikNoteObject from '@/components/forms/fields/FormikNoteObject';
import DevJSONView from '@/components/DevJSONView';
import { safelyParseDate } from '@/utils/date';

const parseEmail = (contact) =>
	contact?.email ?? contact?.address ?? contact?.emailAddress?.address ?? '';

const parseName = (contact) => {
	const splitName = (contact?.name ?? contact?.emailAddress?.name)?.split(' ') ?? [];
	return {
		firstName: splitName[0] ?? '',
		lastName: splitName.slice(1).join(' ') ?? '',
	};
};

const TaskDataWrapper = ({ dialogData = {} }) => {
	const userPrefs = useSelector((state) => state.userPrefs);
	const timezone = userPrefs?.timezone;

	const dispatch = useDispatch();
	const params = useParams();
	const isClient = Boolean(params?.clientId);
	const isRelationshipCompany = Boolean(params?.relationshipCompanyId);

	const { clientRow: contextClientRow, dealRow, relationshipCompany } = useObjectContext();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const [updateTask, updateTaskMutation] = useUpdateTaskMutation();

	const taskDialogData = dialogData?.task ?? dialogData?.engagement ?? {};

	const taskId = taskDialogData?.id;
	const ownerDocId = taskDialogData?.ownerDocId;

	const initialTaskType = useMemo(() => {
		return taskDialogData?.taskType ?? taskTypes[0];
	}, [taskDialogData?.taskType]);

	const { data: taskUserData } = useGetUserDetailsQuery(
		{ userId: ownerDocId },
		{
			skip: !ownerDocId,
		},
	);

	const userQuery = useGetUserByEmailQuery(
		{
			email: taskUserData?.email,
		},
		{ skip: !taskUserData?.email },
	);
	const userId = userQuery.data;

	const graphEventQuery = useGetEventDataQuery(
		{
			taskData: taskDialogData?.taskType,
			eventId: taskDialogData?.graphEventId,
		},
		{ skip: !taskDialogData?.taskType || !taskDialogData?.graphEventId },
	);
	const eventTaskMsData = graphEventQuery.data ?? {};

	const loadingMsData =
		taskId &&
		((!userQuery?.isError && !userQuery?.isSuccess) ||
			(!graphEventQuery?.isError && !graphEventQuery?.isSuccess));

	const [createEventTask, createEventMutation] = useCreateEventMutation();
	const [updateGraphEventData, updateEventMutation] = useUpdateEventMutation();

	const { data: taskDealRow } = useGetDealRowQuery(taskDialogData?.dealId, {
		skip: !taskDialogData?.dealId,
	});
	const clientId = taskDialogData?.clientId ?? getDealClientId(taskDealRow);
	const { data: taskClientData } = useGetClientPropertiesQuery(clientId, {
		skip: !clientId,
	});

	const initialAttendees = useMemo(
		() =>
			(eventTaskMsData?.attendees ?? []).length > 0
				? eventTaskMsData.attendees.map((contact) => {
						const email = contact?.emailAddress?.address ?? contact.email;
						const names = contact?.emailAddress?.name?.split(' ') ?? [];
						return {
							id: email,
							email,
							firstName: names[0] ?? '',
							lastName: names.slice(1).join(' ') ?? '',
						};
					})
				: (taskDialogData?.attendees ?? []).map((contact) => {
						const email = parseEmail(contact);
						const { firstName, lastName } = parseName(contact);

						return {
							id: email,
							email,
							firstName,
							lastName,
						};
					}),
		[eventTaskMsData?.attendees, taskDialogData?.attendees],
	);

	const date = useMemo(
		() => (taskDialogData?.dueDate ? safelyParseDate(taskDialogData.dueDate) : new Date()),
		[taskDialogData?.dueDate],
	);

	const { startTime, endTime } = useMemo(() => {
		let startTime;
		let endTime;

		if (taskDialogData?.startTime && taskDialogData?.endTime) {
			startTime = safelyParseDate(taskDialogData.startTime);
			endTime = safelyParseDate(taskDialogData.endTime);
		} else {
			const now = new Date();
			const minutes = now.getMinutes();
			startTime = new Date(now);

			if (minutes < 30) {
				startTime.setMinutes(30, 0, 0);
			} else {
				startTime.setHours(startTime.getHours() + 1, 0, 0, 0);
			}

			// A new Date: safelyParseDate returns the same instance, which made start and end equal
			endTime = new Date(startTime);
			endTime.setMinutes(endTime.getMinutes() + 30);
		}

		return { startTime, endTime };
	}, [taskDialogData?.startTime, taskDialogData?.endTime]);

	const objectType = isClient
		? 'client'
		: isRelationshipCompany
			? 'relationshipCompany'
			: dealRow?.objectType;

	const initialValues = useMemo(() => {
		return {
			title: taskDialogData?.subject ?? '',
			dueDate: date,
			priority: taskDialogData?.priority ?? 'NONE',
			notes: taskDialogData?.body ?? ' ',
			taskType: initialTaskType,
			errorMessage: '',
			deal: isRelationshipCompany ? null : (taskDealRow ?? dealRow),
			client: isRelationshipCompany
				? null
				: (taskClientData?.clientRow ?? contextClientRow ?? dialogData?.client),
			objectType,
			ownerDocId: ownerDocId ?? userDetails?.userId,
			// Existing tasks resolve their stored owner via taskUserData; new tasks
			// default to the current user, whom we already have on hand.
			ownerUser: ownerDocId ? taskUserData : userDetails,
			attendees: initialAttendees,
			userId: null,
			isOnlineMeeting: taskDialogData?.isOnlineMeeting ?? false,
			startTime,
			address:
				eventTaskMsData?.location?.displayName ??
				eventTaskMsData?.locations?.[0]?.displayName ??
				taskDialogData?.address ??
				'',
			endTime,
			isExistingTask: Boolean(taskId),
			createCalendarEvent: !taskId && initialTaskType === 'MEETING' ? true : false,
			category: taskDialogData?.category ?? null,
		};
	}, [
		taskDialogData,
		taskDealRow,
		taskClientData?.clientRow,
		contextClientRow,
		initialAttendees,
		dialogData,
		eventTaskMsData,
		taskId,
		initialTaskType,
		userDetails,
		ownerDocId,
		taskUserData,
		objectType,
		date,
		startTime,
		endTime,
	]);

	const [createTask, createTaskMutation] = useCreateTaskMutation();

	const handleClose = () => {
		dispatch(closeDialog('task'));
	};

	const handleTaskSubmission = async ({ graphId, values }) => {
		const attendees = values.attendees.map((contact) => ({
			emailAddress: {
				address: contact?.email,
				name: `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim(),
			},
		}));

		console.log('Values before creating', values);

		const dealId = isRelationshipCompany ? undefined : values.deal?.dealId;
		const relationshipCompanyId = isRelationshipCompany
			? params.relationshipCompanyId
			: undefined;
		const clientId = values.client?.clientId;
		const pipeline = values.deal?.pipeline;
		const type = isRelationshipCompany
			? 'relationshipCompany'
			: (values.deal?.objectType ?? objectType);

		const startTime = values.startTime.toISOString();
		const endTime = values.endTime.toISOString();

		const body = {
			title: values.title.trim(),
			dueDate: values.endTime,
			priority: values.priority,
			notes: (values.notes ?? '').trim(),
			ownerDocId: values.ownerDocId,
			type: values.taskType,
			...(type && { objectType: type }),
			...(relationshipCompanyId && { relationshipCompanyId }),
			...(dealId && { dealId }),
			...(clientId && { clientId }),
			...(graphId && { graphId }),
			...((userId || account?.homeAccountId) && {
				graphUserId: userId ?? account?.homeAccountId,
			}),
			...(attendees.length > 0 && { attendees }),
			startTime,
			endTime,
			...((values.address ?? '').length > 0 &&
				!values.isOnlineMeeting && { address: values.address }),
			isOnlineMeeting: values.isOnlineMeeting,
			createCalendarEvent: values.createCalendarEvent ?? false,
			category: values.category ?? null,
		};

		console.log('Create/update task body', body);
		if (!values.isExistingTask) {
			const response = await createTask(body).unwrap();
			return { response, body: { ...body, pipeline } };
		} else {
			const res = await updateTask({
				taskId,
				clientId,
				existingClientId: taskDialogData?.clientId,
				existingDeal: taskDealRow,
				selectedDeal: values.deal,
				objectType: values?.deal?.objectType,
				properties: {
					subject: values.title.trim(),
					body: values.notes.trim(),
					ownerDocId: values.ownerDocId,
					timestamp: getUnixTime(values.endTime) * 1000,
					priority: values.priority.toUpperCase(),
					taskType: values.taskType.toUpperCase(),
				},
				...(attendees.length > 0
					? { attendees }
					: taskDialogData?.attendees
						? { attendees: FieldValue.delete() }
						: {}),
				startTime,
				endTime,
				dueDate: values.dueDate,
				...((values.address ?? '').length > 0 &&
					!values.isOnlineMeeting && { address: values.address }),
				isOnlineMeeting: values.isOnlineMeeting,
				category: values.category ?? null,
			}).unwrap();

			return { res, body };
		}
	};

	const handleEditTask = async ({ values, resetForm, setFieldValue, eventBody }) => {
		console.log('Editing task!');

		try {
			if (eventTaskMsData?.id) {
				const updatedEventRes = await updateGraphEventData({
					taskType: initialTaskType,
					eventId: eventTaskMsData?.id,
					eventData: eventBody,
				}).unwrap();
				console.log('Updated event response: ', updatedEventRes);
			} else {
				console.log('Could not find MS Graph event to update');
			}
		} catch (e) {
			console.log('Could not update MS Graph event task', e);
		}

		let res;
		try {
			res = await handleTaskSubmission({ values });
		} catch (err) {
			if (err?.status === 404) {
				console.log(
					'🙅 ~ Task no longer exists, closing dialog and refreshing search',
					err,
				);
				dispatch(
					showToast({
						message:
							err?.error ?? 'This task could not be found. It may have been deleted.',
						error: true,
					}),
				);
				dispatch(triggerRefresh());
				handleClose();
				return;
			}
			throw err;
		}
		console.log('here is the update task res: ', res);
		//set deal or client id to null if no client or deal selected
		// Update the task
		if (res) {
			console.log('🚀 ~ Successfully updated task', res);

			// Navigate or show success message as before
			const path = window.location.pathname;
			let newPath, params;
			if (values.deal) {
				const dealPipeline = values.deal.pipeline;
				const pathName = getUrlFromPipeline(dealPipeline);
				newPath =
					pathName && values.deal?.dealId ? `${pathName}/${values.deal.dealId}` : path;
				params = { state: { task: taskId } };
			} else if (values.client) {
				newPath = values.client?.clientId ? `/clients/${values.client.clientId}` : path;
				params = { state: { task: taskId } };
			}

			dispatch(
				showToast({
					message: 'Task updated successfully!',
					autohide: true,
					...(path.startsWith('/my-tasks') &&
						newPath && {
							autohide: false,
							action: {
								path: `${newPath}/tasks`,
								params,
								label: 'GO TO TASK',
							},
						}),
				}),
			);
			resetForm();
			handleClose();
		} else {
			console.log('🙅 ~ Error updating task');
			setFieldValue('errorMessage', 'Error updating task. Please try again');
		}
	};

	const createMSCalendarEvent = async ({ values, eventBody }) => {
		const owner = values.ownerUser;

		if (!owner) {
			console.error(
				`Could not create MS calendar event: no owner user resolved for ownerDocId ${values.ownerDocId}`,
			);
			return;
		}

		const attendees = [
			{
				emailAddress: {
					address: owner.email,
					name: owner.name,
				},
			},
		];

		const alteredEventBody = {
			...eventBody,
			...(values.taskType !== 'MEETING' && {
				attendees,
			}),
		};

		const eventRes = await createEventTask({
			taskType: values.taskType,
			eventDetails: alteredEventBody,
		}).unwrap();
		return eventRes?.id;
	};

	const handleCreateTask = async ({ values, setFieldValue, resetForm, eventBody }) => {
		console.log('Creating task!', eventBody);

		const graphEventId = values.createCalendarEvent
			? await createMSCalendarEvent({ values, eventBody })
			: null;

		const updatedTaskType = values.taskType === 'MEETING' ? 'TODO' : values.taskType;
		setFieldValue('taskType', updatedTaskType);

		const res = await handleTaskSubmission({
			graphId: graphEventId,
			values,
		});

		if (res) {
			console.log('🚀 ~ Successfully created task', res);
			const newTaskId = res.response?.taskId;
			const path = window.location.pathname;
			const { clientId, dealId, pipeline } = res.body;
			let newPath, params;
			if (dealId) {
				const pathName = getUrlFromPipeline(pipeline);
				newPath = pathName ? `${pathName}/${dealId}` : path;
				params = { state: { task: newTaskId } };
			} else if (clientId) {
				newPath = `/clients/${clientId}`;
				params = { state: { task: newTaskId } };
			}

			dispatch(
				showToast({
					message: 'Task created!',
					autohide: true,
					...(path.startsWith('/my-tasks') &&
						newPath && {
							autohide: false,
							action: {
								path: `${newPath}/tasks`,
								params,
								label: 'GO TO TASK',
							},
						}),
				}),
			);
			resetForm();
			handleClose();
		} else {
			console.log('🙅 ~ Error creating task');
			setFieldValue('errorMessage', 'Error creating task. Please try again');
		}
	};

	const [formLoading, setFormLoading] = useState(false);

	return (
		<Formik
			{...{
				initialValues,
				validationSchema,
				enableReinitialize: true,
				validateOnBlur: true,
				onSubmit: async (values, { resetForm, setFieldValue, setSubmitting }) => {
					console.log('📋 ~ Formik values', values);
					setFormLoading(true);
					setSubmitting(true);

					const startTime = values.startTime.toISOString();
					const endTime = values.endTime.toISOString();

					const taskWithClient =
						values?.client?.name?.length > 0
							? `${values?.client?.name} - ${values.title}`
							: values.title;

					const attendees = values.attendees.map((contact) => ({
						emailAddress: {
							address:
								contact?.contactEmail || contact?.email || contact?.emailAddress,
							name:
								contact?.contactName ||
								`${contact?.firstName || ''} ${
									contact?.lastName || contact?.name || ''
								}`.trim(),
						},
					}));

					console.log('Here is the time zone for the task/event', timezone);
					console.log('Here are the attendees', attendees);

					const eventBody = {
						subject: taskWithClient,
						body: {
							contentType: 'HTML',
							content: values.notes ?? '',
						},
						start: {
							dateTime: startTime,
							timeZone: timezone?.windows, //'New Zealand Standard Time',
						},
						end: {
							dateTime: endTime,
							timeZone: timezone?.windows, // 'New Zealand Standard Time',
						},
						location: {
							displayName: values.address,
						},
						attendees,
						isOnlineMeeting: values.isOnlineMeeting || false,
					};

					try {
						const fn = values.isExistingTask ? handleEditTask : handleCreateTask;

						await fn({
							values,
							resetForm,
							setFieldValue,
							eventBody,
						});
					} catch (err) {
						console.log('🙅 ~ Error occurred', err);
						setFieldValue(
							'errorMessage',
							err?.error ?? err?.data?.error ?? 'An error occurred. Please try again',
						);
					} finally {
						setFormLoading(false);
						setSubmitting(false);
					}
				},
			}}
		>
			{(formik) => {
				const {
					values,
					touched,
					errors,
					getFieldProps,
					handleSubmit,
					resetForm,
					isSubmitting,
					setFieldValue,
					setTouched,
					validateForm,
				} = formik;

				const formDisabled =
					formLoading ||
					[
						updateTaskMutation,
						createTaskMutation,
						updateEventMutation,
						createEventMutation,
					].some((f) => f.isLoading) ||
					isSubmitting;

				return (
					<Form>
						<FormikNoteObject />
						<Dialog
							open={true}
							onClose={(e, reason) => {
								if (formLoading) return;
								handleClose();
							}}
							scroll='paper'
							hideBackdrop
							disableEnforceFocus
							sx={{
								'& .MuiPaper-root': {
									minWidth: '50%',
									maxHeight: '90vh',
									display: 'flex',
									flexDirection: 'column',
								},
							}}
						>
							<DialogTitle>
								{`${values.isExistingTask ? 'Update' : 'Create'} Task`}
							</DialogTitle>
							<DialogContent
								sx={{
									flex: 1,
									overflowY: 'auto',
									paddingBottom: '2em',
								}}
							>
								<Grid container spacing={1}>
									<Grid size={4}>
										<FormLabel>{'Task Type'}</FormLabel>
										<TaskTypeSelect
											disabled={formDisabled || values.isExistingTask}
										/>
									</Grid>
									<Grid size={4}>
										<CategorySelect
											disabled={formDisabled}
											category={values.category}
											setCategory={(value) =>
												setFieldValue('category', value)
											}
										/>
									</Grid>
									<Grid size={4}>
										<PrioritySelect
											disabled={formDisabled}
											priority={values.priority}
											setPriority={(value) =>
												setFieldValue('priority', value)
											}
										/>
									</Grid>
									<DateFields disabled={formDisabled} />
									<MeetingFields disabled={formDisabled} />
									<Grid size={12}>
										<FormLabel required>{'Title'}</FormLabel>
										<PerformantTextField
											fullWidth={true}
											{...getFieldProps('title')}
											error={touched.title && Boolean(errors.title)}
											helperText={touched.title && errors.title}
											disabled={formDisabled}
											required
											slotProps={{
												htmlInput: { 'data-testid': 'taskTitle' },
											}}
										/>
									</Grid>
									{/* TODO: Remove this once we have a relationship company select that can be used to create task in the general view */}
									{isRelationshipCompany ? (
										<Grid size={12}>
											<FormLabel>Company</FormLabel>
											<TextField disabled fullWidth value={relationshipCompany?.name ?? ''} />
										</Grid>
									) : (
										<AssociatedClientDealSelect
											clientId={
												values.client?.clientId ?? getDealClientId(dealRow)
											}
											clientDisabled={
												formDisabled ||
												Boolean(params?.dealId) ||
												isClient ||
												values.isExistingTask
											}
											dealDisabled={
												formDisabled ||
												Boolean(params?.dealId) ||
												values.isExistingTask
											}
											onClientChange={(newClient) => {
												setFieldValue('client', newClient);
												console.log('NEW CLIENT', newClient);
												if (newClient) {
													setFieldValue('deal', null);
												}
											}}
											onDealChange={(newDeal) => {
												setFieldValue('deal', newDeal);
												if (newDeal) {
													setFieldValue('objectType', newDeal.objectType);
												}
											}}
										/>
									)}
									<Grid size={12}>
										<FormLabel>{'Assignee'}</FormLabel>
										<OwnerSelect
											initialId={values.ownerDocId}
											disabled={formDisabled}
											showGroup={true}
											onChange={(user) => {
												setFieldValue('ownerDocId', user?.id ?? null);
												setFieldValue('ownerUser', user ?? null);
												const updatedAttendees = [...values.attendees];
												if (user && !updatedAttendees.includes(user)) {
													updatedAttendees.push(user);
												}
												setFieldValue('attendees', updatedAttendees);
											}}
											showEmail={true}
										/>
									</Grid>
									<Grid size={12}>
										<FormLabel>{'Task Info'}</FormLabel>
										<PerformantTextField
											fullWidth={true}
											multiline
											rows={10}
											{...getFieldProps('notes')}
											disabled={formDisabled}
										/>
									</Grid>
									{!values.isExistingTask && (
										<Grid size={12} sx={{ mt: 1 }}>
											<FolioCheckbox
												label={
													<Box display='flex' alignItems='center'>
														<Box
															component='img'
															src={outlookLogo}
															sx={{
																width: 18,
																height: 18,
																mr: 1,
															}}
														/>
														Add Event to Outlook Calendar
													</Box>
												}
												checked={values.createCalendarEvent}
												onChange={(e) =>
													setFieldValue(
														'createCalendarEvent',
														e.target.checked,
													)
												}
												disabled={
													formDisabled || values.taskType === 'MEETING'
												}
											/>
										</Grid>
									)}
								</Grid>
								{!loadingMsData &&
									taskId &&
									Object.keys(eventTaskMsData).length === 0 && (
										<FormHelperText>
											Could not find Outlook event info; some fields may not
											pre-populate correctly
										</FormHelperText>
									)}
								{values.errorMessage.length > 0 && (
									<FormHelperText error>{values.errorMessage}</FormHelperText>
								)}
								<DevJSONView value={values} />
							</DialogContent>
							<DialogActions>
								<Button
									disabled={formLoading}
									onClick={(e) => {
										e.preventDefault();
										resetForm();
										handleClose();
									}}
									color='primary'
								>
									Cancel
								</Button>
								<Button
									color='primary'
									onClick={async (e) => {
										e.preventDefault();
										// Formik's own submit touches fields by walking values, which
										// skips Date objects it has already seen, so touch each
										// validated field explicitly to show its error
										setTouched(
											Object.fromEntries(
												Object.keys(validationSchema.fields).map((field) => [
													field,
													true,
												]),
											),
											false,
										);
										const validationErrors = await validateForm();
										if (Object.keys(validationErrors).length === 0) {
											handleSubmit();
										}
									}}
									variant='contained'
									disabled={formLoading || formDisabled}
								>
									{formLoading ? (
										<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
									) : values.isExistingTask ? (
										'Update'
									) : (
										'Create'
									)}
								</Button>
							</DialogActions>
						</Dialog>
					</Form>
				);
			}}
		</Formik>
	);
};

export const AddTaskDialog = () => {
	const dialog = useSelector((state) => state.dialog);
	const open = dialog?.open?.task;

	const dialogData = dialog?.data?.task ?? {};

	if (!open) return <></>;
	return <TaskDataWrapper dialogData={dialogData} />;
};
